import { Component, inject, OnInit } from '@angular/core';
import { OptionsList } from 'gung-common';
import { AuthService, CheckoutStepComponent, Customer, CustomerContact, CustomerContactService, MetadataService, TableRecord } from 'gung-standard';
import { DeliveryLocationsService, JeevesDeliveryLocation } from 'gung-standard-jeeves';
import { SelectedCustomerService } from 'projects/gung-standard/src/public-api';
import { first, forkJoin, map, of, switchMap } from 'rxjs';
import { HlDisplayModalService } from '../../../services/hl-display-modal.service';
import { CheckoutSettingsGroup, HlDisplayPortalFeaturesService } from '../../../services/hl-display-portal-features.service';

@Component({
  selector: 'app-hl-display-customer-service-shipping-step-portal',
  templateUrl: './hl-display-customer-service-shipping-step-portal.component.html',
  styleUrl: './hl-display-customer-service-shipping-step-portal.component.css',
  standalone: false
})
export class HlDisplayCustomerServiceShippingStepPortalComponent extends CheckoutStepComponent implements OnInit {
  protected selectedCustomerService = inject(SelectedCustomerService);
  protected hlDisplayModalService = inject(HlDisplayModalService);
  protected deliveryLocationsService = inject(DeliveryLocationsService);
  protected customerContactService = inject(CustomerContactService);
  protected metadataService = inject(MetadataService);
  protected authService = inject(AuthService);
  protected hlDisplayPortalFeaturesService = inject(HlDisplayPortalFeaturesService);

  protected selectedCustomer: Customer;
  public pointOfDeliveryOptions: OptionsList[] = [];
  private deliveryLocations: JeevesDeliveryLocation[];
  protected customerContacts: CustomerContact[];
  public customerContactsOptions: OptionsList[] = [];
  protected customerSpecificTexts: { id: string; name: string }[] = [];
  public customerSpecificTextOptions: OptionsList[] = [];
  public deliveryDateOption;
  public orderConfirmError = false;
  public trackAndTraceError = false;
  protected isCustomerService = false;
  protected useOrderSpecificTextDropdown: boolean = false;

  public showCarrierWarning: boolean = false;
  public showDeliveryInstructionWarning: boolean = false;

  private carrierCodesToDisplayContactWarning: string[] = [];
  private carrierCodesToDisplayDeliveryInstructionWarning: string[] = [];

  ngOnInit(): void {
    if (!!this.checkout.extra.oh.q_earlier && !this.checkout.extra.oh.q_fixed) {
      this.deliveryDateOption = 'EARLIER';
    } else if (!this.checkout.extra.oh.q_earlier && !!this.checkout.extra.oh.q_fixed) {
      this.deliveryDateOption = 'FIXED';
    } else if (this.checkout.extra.oh.q_earlier === 'N' && this.checkout.extra.oh.q_fixed === 'N') {
      this.deliveryDateOption = 'N';
    } else {
      this.deliveryDateOption = 'EARLIER';
    }

    super.ngOnInit();
    this.onNextBtnClicked.subscribe(_ => this.handleNextButtonClicked());

    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(
        first(),
        switchMap(selectedCustomer =>
          forkJoin({
            customerContacts: this.customerContactService.getCustomerContacts(selectedCustomer.id).pipe(first()),
            selectedCustomer: of(selectedCustomer),
            deliveryLocations: this.deliveryLocationsService
              .getDeliveryLocationsForCustomer(selectedCustomer.id)
              .pipe(first()),
            customerSpecificTexts: of(
              this.metadataService
                .getMetadataTableList('xktx')
                .filter(entry => entry.ftgnr === selectedCustomer.id)
                .map(entry => {
                  return { id: entry.kundtextkod, name: entry.edit };
                })
            ),
            isCustomerService: this.authService.getHasRoles('CUSTOMER_SERVICE').pipe(first()),
            checkoutSettingsGroup: this.hlDisplayPortalFeaturesService
              .getPortalFeatureGroup('orderCheckoutSettingsGroup')
              .pipe(
                first(),
                map(checkoutSettingsGroup => checkoutSettingsGroup as CheckoutSettingsGroup)
              )
          })
        )
      )
      .subscribe(
        ({
          customerContacts,
          selectedCustomer,
          deliveryLocations,
          customerSpecificTexts,
          isCustomerService,
          checkoutSettingsGroup
        }) => {
          this.isCustomerService = isCustomerService;
          this.selectedCustomer = selectedCustomer;

          this.customerContacts = customerContacts;
          this.customerSpecificTexts = customerSpecificTexts;
          // Map delivery locations and split ordlevplats1
          this.pointOfDeliveryOptions = this.mapDeliveryLocations(deliveryLocations);
          this.customerContactsOptions = this.mapCustomerContacts(customerContacts);
          this.customerSpecificTextOptions = this.mapCustomerSpecificTexts(customerSpecificTexts);

          this.deliveryLocations = deliveryLocations;

          this.useOrderSpecificTextDropdown = checkoutSettingsGroup.useOrderSpecificTextDropdown;
          this.carrierCodesToDisplayContactWarning = checkoutSettingsGroup.carrierCodesToDisplayContactWarning || [];
          this.carrierCodesToDisplayDeliveryInstructionWarning =
            checkoutSettingsGroup.carrierCodesToDisplayDeliveryInstructionWarning || [];

          this.setInitialValues();
        }
      );

    // This is necessary to remove all default values assigned during the default Jeeves step for addresses in assign defaults.
    this.cleanProcargs(this.checkout);
  }

  /**
   * Removes fields from checkout.extra.procargs that exist in JeevesOrderDeliveryAddress.addressValues.
   * @param checkout The checkout object containing extra.procargs.
   */
  public cleanProcargs(checkout: any): void {
    if (!checkout?.extra?.procargs) {
      return; // Exit if procargs is not defined
    }

    // Get the keys from JeevesOrderDeliveryAddress.addressValues
    const addressKeys = [
      'ordlevadr1',
      'ordlevadr2',
      'ordlevadr3',
      'ordlevadr4',
      'ftgpostnr',
      'ordlevadrbstort',
      'ordlevadrlandskod',
      'ordlevplats1',
      'levsattkod',
      'levvillkkod'
    ];

    // Remove matching keys from procargs
    for (const key of addressKeys) {
      if (key in checkout.extra.procargs) {
        delete checkout.extra.procargs[key];
      }
    }
  }

  public selectDeliveryAddress(id: string): void {
    if (id == null) {
      return;
    }
    const selectedDeliveryLocation = this.deliveryLocations.find(dl => dl.extra?.lp?.ordlevplats1 === id);
    this.checkout.extra.oh = {
      ...this.checkout.extra.oh,
      ordlevplats1: selectedDeliveryLocation.extra?.lp?.ordlevplats1 || ''
    };

    this.checkout.extra.procargs = {
      ...this.checkout.extra.procargs,
      ordlevadr1: selectedDeliveryLocation?.extra?.fr?.ftgpostadr1 || '',
      ordlevadr2: selectedDeliveryLocation?.extra?.fr?.ftgpostadr2 || '',
      ftgpostnr: selectedDeliveryLocation?.extra?.fr?.ftgpostnr || '',
      ordlevadr3: selectedDeliveryLocation?.extra?.fr?.ftgpostadr3 || ''
    };

    const { q_hl_logentcontactperson, q_hl_logentcontactdetails, q_hl_logentdeliveryinstruction, q_hl_deliverytimeearliest, q_hl_deliverytimelatest, ...ohWithoutTransportDetails } = this.checkout.extra.oh;
    this.checkout.extra.oh = ohWithoutTransportDetails;

    this.setTransportationDetails();
  }

  public getLabel(): string {
    return 'SHIPPING';
  }

  protected handleNextButtonClicked(): void {
    if (!!this.checkout.extra._msFlowOrderConfirmEmail && !this.checkout.extra._msFlowOrderConfirmEmail.includes('@')) {
      this.orderConfirmError = true;
      return;
    } else if (!this.checkout.extra._msFlowOrderConfirmEmail) {
      this.orderConfirmError = true;
      return;
    }

    if (!this.checkout.extra.oh.salestype) {
      return;
    }

    if (!!this.checkout.extra.oh.q_hl_emailtt && !this.checkout.extra.oh.q_hl_emailtt.includes('@')) {
      this.trackAndTraceError = true;
      return;
    }

    if (!!this.checkout.extra.oh.q_hl_emailtt) {
      if (this.checkout.extra.oh.q_hl_sendttlinkemail !== '1') {
        this.checkout.extra.oh.q_hl_sendttlinkemail = '1';
      }
    } else {
      delete this.checkout.extra.oh.q_hl_emailtt;
      delete this.checkout.extra.oh.q_hl_sendttlinkemail;
    }

    this.ensureNoInvalidEmptyStrings();

    this.stepDone.emit(this.checkout);
  }

  private ensureNoInvalidEmptyStrings(): void {
    this.checkout.extra.oh = {
      ...this.checkout.extra.oh,
      ordertextkod: this.checkout.extra.oh.ordertextkod || undefined,
      transportorskod: this.checkout.extra.oh.transportorskod || undefined,
      levvillkkod: this.checkout.extra.oh.levvillkkod || undefined,
      dellevtillaten: this.checkout.extra.oh.dellevtillaten || undefined,
      salestype: this.checkout.extra.oh.salestype || undefined,
      q_guarantid: this.checkout.extra.oh.q_guarantid || undefined,
      levsattkod: this.checkout.extra.oh.levsattkod || undefined
    };
  }

  public createPOD(): void {
    this.hlDisplayModalService.openAddEditHlDisplayDeliveryLocation(this.selectedCustomer.id).then(
      () => {
        this.deliveryLocationsService
          .getDeliveryLocationsForCustomer(this.selectedCustomer.id)
          .pipe(first())
          .subscribe(deliveryLocations => {
            this.pointOfDeliveryOptions = this.mapDeliveryLocations(deliveryLocations);
            this.deliveryLocations = deliveryLocations;
          });
      },
      () => {}
    );
  }

  public editTransportDetails(): void {
    this.hlDisplayModalService.openHlDisplayEditTransportDetailsModal(this.checkout).then(
      res => {
        if (!!res) {
          this.checkout.extra.oh = {
            ...this.checkout.extra.oh,
            ...res
          };
        }
      },
      () => {}
    );
  }

  public onDeliveryDateOptionChanged(value: string): void {
    // Update deliveryDateOption
    this.deliveryDateOption = value;

    // Then make changes to checkout.extra.oh based on which option was selected
    if (value === 'EARLIER') {
      this.checkout.extra.oh.q_earlier = true;
      this.checkout.extra.oh.q_fixed = false;
    } else if (value === 'FIXED') {
      this.checkout.extra.oh.q_earlier = false;
      this.checkout.extra.oh.q_fixed = true;
    } else if (value === 'N') {
      this.checkout.extra.oh.q_earlier = 'N';
      this.checkout.extra.oh.q_fixed = 'N';
    }
  }

  private setInitialValues(): void {
    const oh = this.checkout.extra.oh;
    const kus = this.selectedCustomer?.extra?.kus;

    this.checkout.extra.oh = {
      ...oh,
      ordertextkod: oh.ordertextkod ?? (kus?.ordertextkod || undefined),
      transportorskod: oh.transportorskod ?? (kus?.transportorskod || undefined),
      levvillkkod: oh.levvillkkod ?? (kus?.levvillkkod || undefined),
      levsattkod: oh.levsattkod ?? '12', // Should be set to 12 by default as it is the standard road delivery method
      dellevtillaten: oh.dellevtillaten ?? (kus?.dellevtillaten || undefined),
      kundfraktdeb: oh.kundfraktdeb ?? (kus?.kundfraktdeb === '1' || kus?.kundfraktdeb == true),
      q_earlier: oh.q_earlier ?? true,
      q_fixed: oh.q_fixed ?? false,
      ...(this.isCustomerService && kus?.q_hl_editplblob && !oh.editplblob ? { editplblob: kus.q_hl_editplblob } : {})
    };

    this.checkout.extra.oh.q_hl_emailtt = oh.q_hl_emailtt ?? (kus?.q_hl_emailtt || '');
    this.checkout.extra.oh.q_hl_sendttlinkemail = oh.q_hl_sendttlinkemail ?? kus?.q_hl_sendttlinkemail;

    this.checkout.extra.oh.ordertextkod = this.checkout.extra.oh.ordertextkod || kus?.ordertextkod || '';

    // Always let the trade policy decide if we should skip the admin fee by default
    this.checkout.extra._skipAdminFee = this.checkout.extra._skipAdminFee || false;

    if (!oh.ordlevplats1) {
      this.selectDeliveryAddress(kus?.ordlevplats1);
    } else {
      this.setTransportationDetails();
    }


    this.onCarrierChanged(this.checkout.extra.oh.transportorskod);
  }

  setTransportationDetails(): void {
    const oh = this.checkout.extra.oh;
    const kus = this.selectedCustomer?.extra?.kus;
    const lp = this.deliveryLocations?.find(dl => dl.extra?.lp?.ordlevplats1 === oh.ordlevplats1)?.extra?.lp;

    this.checkout.extra.oh = {
      ...oh,
      q_hl_logentcontactperson: oh.q_hl_logentcontactperson ?? lp?.q_hl_logentcontactperson ?? kus?.q_hl_logentcontactperson,
      q_hl_logentcontactdetails: oh.q_hl_logentcontactdetails ?? lp?.q_hl_logentcontactdetails ?? kus?.q_hl_logentcontactdetails,
      q_hl_logentdeliveryinstruction: oh.q_hl_logentdeliveryinstruction ?? lp?.q_hl_logentdeliveryinstruction ?? kus?.q_hl_logentdeliveryinstruction,
      q_hl_deliverytimeearliest: oh.q_hl_deliverytimeearliest ?? lp?.q_hl_deliverytimeearliest ?? kus?.q_hl_deliverytimeearliest,
      q_hl_deliverytimelatest: oh.q_hl_deliverytimelatest ?? lp?.q_hl_deliverytimelatest ?? kus?.q_hl_deliverytimelatest
    };
  }

  public onCarrierChanged(carrierCode: string): void {
    const inDeliveryInstructionList = this.carrierCodesToDisplayDeliveryInstructionWarning.includes(carrierCode);
    const inContactList = this.carrierCodesToDisplayContactWarning.includes(carrierCode);

    this.showDeliveryInstructionWarning = inDeliveryInstructionList;
    this.showCarrierWarning = !inDeliveryInstructionList && inContactList;
  }

  private mapDeliveryLocations(deliveryLocations: JeevesDeliveryLocation[]): OptionsList[] {
    const emptyOption: OptionsList = { id: '', name: '' } as any;
    return [emptyOption, ...deliveryLocations.map(dl => {
      const ordlevplats1 = dl.extra?.lp?.ordlevplats1 || ''; // Ensure ordlevplats1 exists
      const splitOrdlevplats1 = ordlevplats1.split('_').pop(); // Get the part after the underscore

      return {
        id: ordlevplats1,
        name: dl.name,
        ftgpostnr: dl.extra?.fr?.ftgpostnr || '',
        ftgpostadr1: dl.extra?.fr?.ftgpostadr1 || '',
        ftgpostadr2: dl.extra?.fr?.ftgpostadr2 || '',
        ftgpostadr3: dl.extra?.fr?.ftgpostadr3 || '',
        ordlevplats1: splitOrdlevplats1 // Add the transformed value
      };
    })];
  }

  private mapCustomerContacts(customerContacts: CustomerContact[]): OptionsList[] {
    return [{ id: '', name: '' }, ...customerContacts.map(contact => {
      return {
        id: contact.id?.split('@')[0],
        name: contact.name
      };
    })];
  }

  private mapCustomerSpecificTexts(customerSpecificTexts: { id: string; name: string }[]): OptionsList[] {
    return customerSpecificTexts.map(text => {
      return {
        id: text.id,
        name: text.name
      };
    });
  }

  // salestypedescr looks like this "12 - Campaign", we need to sort it by the code part (12 in this case), it can be found on a.value
  public sortMetaDataByCode(a: TableRecord, b: TableRecord): number {
    const aCode = a.value.split(' - ')[0];
    const bCode = b.value.split(' - ')[0];

    if (aCode < bCode) {
      return -1; // a comes before b
    } else if (aCode > bCode) {
      return 1; // a comes after b
    } else {
      return 0; // a and b are equal
    }
  }
}
